import { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { Modal, Button, Badge, EmptyState, DataTable, Column, useToast } from '../../../shared/components';
import { getHumanResourcesRepository } from '../../../shared/services/humanResources/repositoryFactory';
import { CreateCalendarDayInput } from '../../../shared/services/humanResources/types';

// ─── Tipos internos ───────────────────────────────────────────────────────────

interface ParsedRow {
  rowIndex: number;
  calendarDate: string;
  dayOfWeek: string;
  dayType: 'Festivo' | 'Ordinario';
  quincena: string;
  name: string;
  error?: string;
}

interface ImportResult {
  inserted: number;
  skipped: number;
  errors: string[];
}

const WEEKDAYS_ES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const MONTHS_ES   = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
                     'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function weekdayOf(dateStr: string): string {
  return WEEKDAYS_ES[new Date(`${dateStr}T00:00:00Z`).getUTCDay()];
}
function quincenaOf(dateStr: string): string {
  const [, m, d] = dateStr.split('-').map(Number);
  return `${d <= 15 ? '1Q' : '2Q'} ${MONTHS_ES[m - 1]}`;
}
function isValidDate(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s) && !isNaN(Date.parse(`${s}T00:00:00Z`));
}

// ─── Parseo del Excel ─────────────────────────────────────────────────────────

function parseExcelBuffer(buffer: ArrayBuffer): ParsedRow[] {
  const wb = XLSX.read(buffer, { type: 'array', cellDates: true });

  // Intentar usar hoja "CONSOLIDADO" → luego año específico → primera hoja
  let sheetName = wb.SheetNames.find((n) => n.toUpperCase() === 'CONSOLIDADO')
    ?? wb.SheetNames.find((n) => /^(2026|2027|2028)$/.test(n.trim()))
    ?? wb.SheetNames[0];

  const ws = wb.Sheets[sheetName];

  // Leer como matriz plana para detectar dinamicamente la fila de encabezados
  // (el Excel tiene 1-2 filas de titulo antes de los headers reales)
  const raw2d: unknown[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

  // Buscar la fila que contiene "CalendarDate" (máx las primeras 5 filas)
  let headerRowIdx = -1;
  for (let i = 0; i < Math.min(raw2d.length, 5); i++) {
    if (raw2d[i].some((cell) => String(cell).trim().toLowerCase() === 'calendardate')) {
      headerRowIdx = i;
      break;
    }
  }
  if (headerRowIdx === -1) return []; // no encontró encabezados reconocibles

  const headers = raw2d[headerRowIdx].map((h) => String(h).trim());
  const colIdx = (name: string) => headers.findIndex((h) => h.toLowerCase() === name.toLowerCase());

  const raw: Record<string, unknown>[] = raw2d.slice(headerRowIdx + 1).map((row) => {
    const obj: Record<string, unknown> = {};
    headers.forEach((h, i) => { obj[h] = row[i] ?? ''; });
    return obj;
  });

  const results: ParsedRow[] = [];
  void colIdx; // usado implicitamente via headers

  for (let i = 0; i < raw.length; i++) {
    const r = raw[i];

    // Normalizar la fecha (puede venir como string o como Date de Excel)
    let dateStr = '';
    const rawDate = r['CalendarDate'] ?? r['Fecha'] ?? r['calendarDate'] ?? '';
    if (rawDate instanceof Date) {
      // Excel serializa fechas como objetos Date con timezone UTC
      const iso = rawDate.toISOString().slice(0, 10);
      dateStr = iso;
    } else if (typeof rawDate === 'number') {
      // Número serial de Excel
      const d = XLSX.SSF.parse_date_code(rawDate);
      dateStr = `${d.y}-${String(d.m).padStart(2,'0')}-${String(d.d).padStart(2,'0')}`;
    } else {
      dateStr = String(rawDate).trim();
    }

    if (!dateStr || dateStr === '0') continue; // fila vacía
    if (dateStr.toLowerCase().includes('calendardate')) continue; // encabezado repetido

    // Normalizar tipo de día
    const rawType = String(r['DayType'] ?? r['Tipo'] ?? r['dayType'] ?? 'Festivo').trim();
    const dayType: 'Festivo' | 'Ordinario' =
      rawType.toLowerCase().includes('fest') ? 'Festivo' : 'Ordinario';

    // Derivar DayOfWeek y Quincena automáticamente si no vienen o son incorrectos
    const parsedDow = isValidDate(dateStr) ? weekdayOf(dateStr) : '';
    const parsedQ   = isValidDate(dateStr) ? quincenaOf(dateStr) : '';

    const dayOfWeek = parsedDow || String(r['DayOfWeek'] ?? r['DiaSem'] ?? '').trim();
    const quincena  = parsedQ   || String(r['Quincena']  ?? '').trim();
    const name      = String(r['Name'] ?? r['Nombre'] ?? r['Festivo'] ?? '').trim();

    const error = !isValidDate(dateStr)
      ? `Fecha inválida: "${dateStr}"`
      : !dayOfWeek
      ? `No se pudo calcular DíaSem para ${dateStr}`
      : undefined;

    results.push({ rowIndex: i + 2, calendarDate: dateStr, dayOfWeek, dayType, quincena, name, error });
  }

  return results;
}

// ─── Componente principal ─────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

type Step = 'upload' | 'preview' | 'importing' | 'done';

const repo = getHumanResourcesRepository();

export function CalendarExcelImportModal({ open, onClose, onImported }: Props) {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('upload');
  const [isDragging, setIsDragging] = useState(false);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [progress, setProgress] = useState(0);

  const reset = () => {
    setStep('upload');
    setRows([]);
    setResult(null);
    setProgress(0);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClose = () => { reset(); onClose(); };

  const processFile = useCallback((file: File) => {
    if (!file.name.match(/\.(xlsx|xls|xlsm)$/i)) {
      toast.show('Solo se aceptan archivos Excel (.xlsx, .xls).', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = parseExcelBuffer(e.target!.result as ArrayBuffer);
        if (parsed.length === 0) {
          toast.show('El archivo no tiene filas con datos reconocibles.', 'error');
          return;
        }
        setRows(parsed);
        setStep('preview');
      } catch (err) {
        toast.show(`Error al leer el Excel: ${err instanceof Error ? err.message : 'desconocido'}`, 'error');
      }
    };
    reader.readAsArrayBuffer(file);
  }, [toast]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const validRows  = rows.filter((r) => !r.error);
  const errorRows  = rows.filter((r) => r.error);
  const festivoRows = validRows.filter((r) => r.dayType === 'Festivo');
  const ordinarioRows = validRows.filter((r) => r.dayType === 'Ordinario');

  const handleImport = async () => {
    if (validRows.length === 0) return;
    setStep('importing');
    setProgress(30);

    const inputs: CreateCalendarDayInput[] = validRows.map((r) => ({
      calendarDate: r.calendarDate,
      dayOfWeek:    r.dayOfWeek,
      dayType:      r.dayType,
      quincena:     r.quincena,
    }));

    let inserted = 0;
    let skipped  = 0;
    const errors: string[] = [];

    try {
      setProgress(60);
      const result = await repo.createCalendarDaysBulk(inputs);
      inserted = result.inserted;
      skipped  = result.skipped;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      errors.push(msg);
    }
    setProgress(100);

    setResult({ inserted, skipped, errors });
    setStep('done');
    if (inserted > 0) onImported();
  };

  // ─── Columnas de preview ────────────────────────────────────────────────────
  const columns: Column<ParsedRow>[] = [
    { key: 'calendarDate', header: 'Fecha',    render: (r) => <span className="font-mono text-xs">{r.calendarDate}</span> },
    { key: 'dayOfWeek',    header: 'Día Sem',  render: (r) => r.dayOfWeek },
    { key: 'name',         header: 'Festivo',  render: (r) => r.name || <span className="text-gray-400">—</span> },
    { key: 'dayType',      header: 'Tipo',     render: (r) => (
      <Badge tone={r.dayType === 'Festivo' ? 'warning' : 'neutral'}>{r.dayType}</Badge>
    )},
    { key: 'quincena',     header: 'Quincena', render: (r) => r.quincena },
    { key: 'status',       header: 'Estado',   render: (r) => r.error
      ? <span className="text-xs text-red-600">⚠ {r.error}</span>
      : <span className="text-xs text-green-600">✓ Válido</span>
    },
  ];

  return (
    <Modal
      open={open}
      onClose={step === 'importing' ? () => {} : handleClose}
      title="📥 Cargar festivos por Excel"
      size="xl"
    >
      {/* ── PASO 1: Upload ────────────────────────────────────────────────────── */}
      {step === 'upload' && (
        <div className="space-y-5">
          {/* Instrucciones de plantilla */}
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm space-y-2">
            <p className="font-semibold text-blue-800">📋 Formato esperado del Excel</p>
            <p className="text-blue-700">
              El archivo debe tener una hoja con encabezados en la primera fila:
            </p>
            <div className="overflow-x-auto">
              <table className="text-xs border-collapse w-full mt-1">
                <thead>
                  <tr className="bg-blue-200 text-blue-900">
                    {['CalendarDate','DayOfWeek','Name','DayType','Quincena'].map(h => (
                      <th key={h} className="px-2 py-1 border border-blue-300 font-mono">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-white text-gray-700">
                    <td className="px-2 py-1 border border-blue-200 font-mono">2026-01-01</td>
                    <td className="px-2 py-1 border border-blue-200">Jueves</td>
                    <td className="px-2 py-1 border border-blue-200">Año Nuevo</td>
                    <td className="px-2 py-1 border border-blue-200 text-red-600 font-medium">Festivo</td>
                    <td className="px-2 py-1 border border-blue-200">1Q Enero</td>
                  </tr>
                  <tr className="bg-gray-50 text-gray-700">
                    <td className="px-2 py-1 border border-blue-200 font-mono">2026-01-12</td>
                    <td className="px-2 py-1 border border-blue-200">Lunes</td>
                    <td className="px-2 py-1 border border-blue-200">Epifanía (Reyes Magos)</td>
                    <td className="px-2 py-1 border border-blue-200 text-red-600 font-medium">Festivo</td>
                    <td className="px-2 py-1 border border-blue-200">1Q Enero</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-blue-600 text-xs">
              💡 <strong>DayOfWeek</strong> y <strong>Quincena</strong> se recalculan automáticamente
              desde <strong>CalendarDate</strong> si hay algún error en esas columnas.
              La columna <strong>Name</strong> es informativa (no se guarda en BD).
            </p>
          </div>

          {/* Zona de drag & drop */}
          <div
            className={[
              'rounded-xl border-2 border-dashed p-8 text-center transition-colors cursor-pointer',
              isDragging ? 'border-brand-400 bg-brand-50' : 'border-ink-200 hover:border-brand-300 hover:bg-ink-50',
            ].join(' ')}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
          >
            <div className="text-4xl mb-2">📊</div>
            <p className="font-medium text-ink-700">Arrastra tu Excel aquí o haz clic para seleccionar</p>
            <p className="text-sm text-ink-400 mt-1">Acepta .xlsx · .xls — hoja CONSOLIDADO, 2026, 2027 o 2028</p>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.xlsm"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={handleClose}>Cancelar</Button>
          </div>
        </div>
      )}

      {/* ── PASO 2: Preview ───────────────────────────────────────────────────── */}
      {step === 'preview' && (
        <div className="space-y-4">
          {/* Resumen */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total filas',   value: rows.length,        color: 'bg-gray-100 text-gray-800' },
              { label: '✓ Válidas',     value: validRows.length,   color: 'bg-green-100 text-green-800' },
              { label: '🎉 Festivos',   value: festivoRows.length, color: 'bg-yellow-100 text-yellow-800' },
              { label: '⚠ Con error',   value: errorRows.length,   color: 'bg-red-100 text-red-800' },
            ].map((s) => (
              <div key={s.label} className={`rounded-xl p-3 text-center ${s.color}`}>
                <div className="text-2xl font-bold">{s.value}</div>
                <div className="text-xs mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          {errorRows.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <p className="font-medium mb-1">⚠ {errorRows.length} fila(s) con error no se importarán:</p>
              <ul className="list-disc list-inside text-xs space-y-0.5">
                {errorRows.slice(0, 5).map((r) => (
                  <li key={r.rowIndex}>Fila {r.rowIndex}: {r.error}</li>
                ))}
                {errorRows.length > 5 && <li>…y {errorRows.length - 5} más</li>}
              </ul>
            </div>
          )}

          {validRows.length === 0 ? (
            <EmptyState
              title="Sin filas válidas"
              description="Revisa el formato del Excel. Ninguna fila pudo procesarse correctamente."
            />
          ) : (
            <DataTable<ParsedRow>
              columns={columns}
              rows={rows}
              rowKey={(r) => `${r.rowIndex}-${r.calendarDate}`}
              emptyTitle="Sin datos"
              pageSize={10}
            />
          )}

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={reset}>← Cambiar archivo</Button>
            <Button variant="outline" onClick={handleClose} className="ml-auto">Cancelar</Button>
            <Button
              variant="primary"
              disabled={validRows.length === 0}
              onClick={handleImport}
            >
              Importar {validRows.length} días al calendario
            </Button>
          </div>
        </div>
      )}

      {/* ── PASO 3: Importando ────────────────────────────────────────────────── */}
      {step === 'importing' && (
        <div className="py-8 space-y-5 text-center">
          <div className="text-5xl animate-bounce">📥</div>
          <p className="font-medium text-ink-800">Importando {validRows.length} días…</p>
          <div className="w-full bg-ink-100 rounded-full h-3 overflow-hidden">
            <div
              className="h-3 rounded-full bg-brand-500 transition-all duration-200"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-sm text-ink-500">{progress}%</p>
        </div>
      )}

      {/* ── PASO 4: Done ──────────────────────────────────────────────────────── */}
      {step === 'done' && result && (
        <div className="space-y-5 py-4">
          <div className="text-center">
            <div className="text-5xl mb-3">{result.inserted > 0 ? '🎉' : '⚠'}</div>
            <p className="font-semibold text-lg text-ink-900">
              {result.inserted > 0
                ? `¡${result.inserted} días importados al calendario!`
                : 'No se insertaron días nuevos.'}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl bg-green-50 border border-green-200 p-3 text-center">
              <div className="text-2xl font-bold text-green-700">{result.inserted}</div>
              <div className="text-xs text-green-600 mt-0.5">Insertados</div>
            </div>
            <div className="rounded-xl bg-yellow-50 border border-yellow-200 p-3 text-center">
              <div className="text-2xl font-bold text-yellow-700">{result.skipped}</div>
              <div className="text-xs text-yellow-600 mt-0.5">Ya existían (omitidos)</div>
            </div>
            <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-center">
              <div className="text-2xl font-bold text-red-700">{result.errors.length}</div>
              <div className="text-xs text-red-600 mt-0.5">Errores</div>
            </div>
          </div>

          {result.errors.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700 space-y-1">
              {result.errors.slice(0, 5).map((e, i) => <p key={i}>• {e}</p>)}
              {result.errors.length > 5 && <p>…y {result.errors.length - 5} más</p>}
            </div>
          )}

          {result.skipped > 0 && (
            <p className="text-xs text-ink-500 text-center">
              Los días omitidos ya tenían registro en el calendario final (unicidad por fecha).
            </p>
          )}

          <div className="flex gap-3 justify-end pt-2">
            {result.inserted > 0 || result.skipped > 0 ? (
              <Button onClick={handleClose}>Cerrar y ver calendario</Button>
            ) : (
              <>
                <Button variant="outline" onClick={reset}>Intentar con otro archivo</Button>
                <Button onClick={handleClose}>Cerrar</Button>
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
